import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  Paper,
  Popper,
  PopperPlacementType,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import ExpandPng from 'assets/images/expand.png';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import {
  endSummaryApi,
  getAllListData,
  getEndPayrollSummaryApi,
  updateData,
} from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT, PAYCODETYPES, PAYSHEET } from 'utils/Constant';
import { postRequest, putRequest } from 'utils/Handlers';

import PayrollTable from './PayrollTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AccordianProps = {
  index: number;
  item: any;
  expanded: string;
  tableData: any;
  userList: any;
  setExpanded: any;
  setEditObject: any;
  hiddenColumns: any;
};

type PopperState = {
  open: boolean;
  placement: any;
  anchorEl: any;
};

const INITIAL_POPPER_STATE: PopperState = {
  anchorEl: null,
  open: false,
  placement: '',
};

// ---------------------------------------------------------------------------
// Shared button / link styles
// ---------------------------------------------------------------------------

const secondaryButtonSx: SxProps = {
  border: '1px solid #E0E0E0',
  color: '#292929',
  fontFamily: 'Red Hat Display Bold',
  marginRight: '0.25em',
  paddingLeft: '10px',
  paddingRight: '10px',
  textTransform: 'capitalize',
};

const primaryButtonSx: SxProps = {
  '&:hover': { backgroundColor: '#BF0000', color: '#FFFFFF' },
  backgroundColor: '#BF0000',
  border: '1px solid #E0E0E0',
  color: '#FFFFFF',
  fontFamily: 'Red Hat Display Bold',
  marginRight: '1em',
  paddingLeft: '10px',
  paddingRight: '10px',
  textTransform: 'capitalize',
};

const syncPaymentButtonSx: SxProps = {
  ...(primaryButtonSx as object),
  height: '40px',
  marginRight: 0,
  minWidth: '120px',
};

const popperMenuLinkStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  color: '#fff',
  fontFamily: 'Red Hat Display Bold',
  fontSize: 15,
  justifyContent: 'flex-start',
  margin: 0,
  padding: 0,
  textDecoration: 'inherit',
  textTransform: 'capitalize',
};

// ---------------------------------------------------------------------------
// CSV header rows
// ---------------------------------------------------------------------------

// ADP export – column meaning is positional (Reg Hours, PBN, PTO, HOL, …).
const csvAdpData: string[] = [
  'Co Code',
  'Batch ID',
  'File #',
  'Reg Hours',
  'Reg Earnings',
  // PBN
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // PTO Hours / Amount
  'Hours 3 Code',
  'Hours 3 Amount',
  // PTO Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // HOL Hours / Amount
  'Hours 3 Code',
  'Hours 3 Amount',
  // HOL Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // SIC Hours / Amount
  'Hours 3 Code',
  'Hours 3 Amount',
  // SIC Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // BON Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // RBN Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // RET Earnings / Amount
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // RET Hours / Amount
  'Hours 3 Code',
  'Hours 3 Amount',
  // OTH / EXP / ADV / MED / DEN / VIS / VLE
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
];

// Current Report / Original Report export header row.
const csvData: string[] = [
  '',
  'Status',
  'Name',
  'Pay Code',
  'Applicant Status',
  'Notes',
  'Base Wage',
  'Base',
  'Co Code',
  'Batch ID',
  'File #',
  'Reg Hours',
  'Gross Earnings',
  'Reg Earnings',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Hours 3 code',
  'Hours 3 amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Hours 3 code',
  'Hours 3 amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Hours 3 code',
  'Hours 3 amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  // RET hours lead the RET earnings pair, matching the row builders.
  'Hours 3 code',
  'Hours 3 amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Earnings 3 Code',
  'Earnings 3 Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
  'Adjust DED Code',
  'Adjust DED Amount',
];

const getCsvFilename = (companyStatus: string): string =>
  companyStatus === 'itech' ? 'EPIHVY01.csv' : 'EPIHB501.csv';

// ---------------------------------------------------------------------------
// CSV row builders
// ---------------------------------------------------------------------------

const buildCurrentReportRow = (item: any, index: number): any[] => [
  index + 1,
  item?.user_workAuth,
  item?.user_lastName + ', ' + item?.user_firstName,
  item?.user_jobWcCode,
  item?.user_consultantStatus,
  item?.summary_notes,
  item?.summary_baseWage,
  item?.summary_basePay,
  item?.user_companyCode,
  item?.summary_batchId,
  item?.user_empCode,
  item?.summary_regHours,
  item?.summary_grossEarnings ? parseFloat(item?.summary_grossEarnings) : 0,
  item?.summary_regEarnings,
  item?.summary_currentEarnings3Code1,
  item?.summary_currentEarnings3Amount1,
  item?.summary_currentHours3Code1,
  item?.summary_currentHours3Amount1,
  item?.summary_currentEarnings3Code2,
  item?.summary_currentEarnings3Amount2,
  item?.summary_currentHours3Code2,
  item?.summary_currentHours3Amount2,
  item?.summary_currentEarnings3Code3,
  item?.summary_currentEarnings3Amount3,
  item?.summary_currentEarnings3Code4,
  item?.summary_currentEarnings3Amount4,
  item?.summary_currentEarnings3Code5,
  item?.summary_currentEarnings3Amount5,
  item?.summary_currentEarnings3Code6,
  item?.summary_currentEarnings3Amount6,
  item?.summary_currentHours3Code3,
  item?.summary_currentHours3Amount3,
  item?.summary_currentEarnings3Code7,
  item?.summary_currentEarnings3Amount7,
  item?.summary_currentHours3Code4,
  item?.summary_currentHours3Amount4,
  item?.summary_currentEarnings3Code8,
  item?.summary_currentEarnings3Amount8,
  item?.summary_currentEarnings3Code9,
  item?.summary_currentEarnings3Amount9,
  item?.summary_currentAdjustDedCode1,
  item?.summary_currentAdjustDedAmount1,
  item?.summary_currentAdjustDedCode2,
  item?.summary_currentAdjustDedAmount2,
  item?.summary_currentAdjustDedCode3,
  item?.summary_currentAdjustDedAmount3,
  item?.summary_currentAdjustDedCode4,
  item?.summary_currentAdjustDedAmount4,
  item?.summary_currentAdjustDedCode5,
  item?.summary_currentAdjustDedAmount5,
  item?.summary_currentAdjustDedCode6,
  item?.summary_currentAdjustDedAmount6,
  item?.summary_currentAdjustDedCode7,
  item?.summary_currentAdjustDedAmount7,
  item?.summary_currentAdjustDedCode8,
  item?.summary_currentAdjustDedAmount8,
  item.summary_psAdjustedAmount,
];

const buildAdpReportRow = (item: any): any[] => [
  item?.user_companyCode,
  item?.summary_batchId,
  item?.user_empCode,
  item?.summary_regHours,
  item?.user_jobWcCode === PAYCODETYPES.HOURLY.id
    ? ''
    : item?.summary_regEarnings,
  // PBN
  item?.summary_currentEarnings3Code1,
  item?.summary_currentEarnings3Amount1,
  // PTO Hours / Amount
  item?.summary_currentHours3Code3,
  item?.summary_currentHours3Amount3,
  // PTO Earnings / Amount
  item?.summary_currentEarnings3Code7,
  item?.summary_currentEarnings3Amount7,
  // HOL Hours / Amount
  item?.summary_currentHours3Code1,
  item?.summary_currentHours3Amount1,
  // HOL Earnings / Amount
  item?.summary_currentEarnings3Code2,
  item?.summary_currentEarnings3Amount2,
  // SIC Hours / Amount
  item?.summary_currentHours3Code2,
  item?.summary_currentHours3Amount2,
  // SIC Earnings / Amount
  item?.summary_currentEarnings3Code3,
  item?.summary_currentEarnings3Amount3,
  // BON Earnings / Amount
  item?.summary_currentEarnings3Code5,
  item?.summary_currentEarnings3Amount5,
  // RBN Earnings / Amount
  item?.summary_currentEarnings3Code9,
  item?.summary_currentEarnings3Amount9,
  // RET Earnings / Amount
  item?.summary_currentEarnings3Code8,
  item?.summary_currentEarnings3Amount8,
  item?.summary_currentHours3Code4,
  item?.summary_currentHours3Amount4,
  // OTH
  item?.summary_currentEarnings3Code6,
  item?.summary_currentEarnings3Amount6,
  // EXP
  item?.summary_currentAdjustDedCode2,
  item?.summary_currentAdjustDedAmount2,
  // ADV
  item?.summary_currentAdjustDedCode8,
  item?.summary_currentAdjustDedAmount8,
  // MED
  item?.summary_currentAdjustDedCode4,
  item?.summary_currentAdjustDedAmount4,
  // DEN
  item?.summary_currentAdjustDedCode5,
  item?.summary_currentAdjustDedAmount5,
  // VIS
  item?.summary_currentAdjustDedCode6,
  item?.summary_currentAdjustDedAmount6,
  // VLE
  item?.summary_currentAdjustDedCode7,
  item?.summary_currentAdjustDedAmount7,
];

const buildOriginalReportRow = (item: any, index: number): any[] => [
  index + 1,
  item?.user_workAuth ? item?.user_workAuth : '',
  item?.user_lastName + ', ' + item?.user_firstName,
  item?.user_jobWcCode,
  item?.user_consultantStatus,
  item?.summary_notes,
  item?.summary_baseWage,
  item?.summary_basePay,
  item?.user_companyCode,
  item?.summary_batchId,
  item?.user_empCode,
  item?.summary_regHours,
  item?.summary_grossEarnings ? parseFloat(item?.summary_grossEarnings) : 0,
  item?.summary_regEarnings,
  item?.summary_currentEarnings3Code1,
  item?.summary_currentEarnings3Amount1,
  item?.summary_currentHours3Code1,
  item?.summary_currentHours3Amount1,
  item?.summary_currentEarnings3Code2,
  item?.summary_currentEarnings3Amount2,
  item?.summary_currentHours3Code2,
  item?.summary_currentHours3Amount2,
  item?.summary_currentEarnings3Code3,
  item?.summary_currentEarnings3Amount3,
  item?.summary_currentEarnings3Code4,
  item?.summary_currentEarnings3Amount4,
  item?.summary_currentEarnings3Code5,
  item?.summary_currentEarnings3Amount5,
  item?.summary_currentEarnings3Code6,
  item?.summary_currentEarnings3Amount6,
  item?.summary_currentHours3Code3,
  item?.summary_currentHours3Amount3,
  item?.summary_currentEarnings3Code7,
  item?.summary_currentEarnings3Amount7,
  item?.summary_currentHours3Code4,
  item?.summary_currentHours3Amount4,
  item?.summary_currentEarnings3Code8,
  item?.summary_currentEarnings3Amount8,
  item?.summary_currentEarnings3Code9,
  item?.summary_currentEarnings3Amount9,
  item?.summary_currentAdjustDedCode1,
  item?.summary_currentAdjustDedAmount1,
  item?.summary_currentAdjustDedCode2,
  item?.summary_currentAdjustDedAmount2,
  item?.summary_currentAdjustDedCode3,
  item?.summary_currentAdjustDedAmount3,
  item?.summary_currentAdjustDedCode4,
  item?.summary_currentAdjustDedAmount4,
  item?.summary_currentAdjustDedCode5,
  item?.summary_currentAdjustDedAmount5,
  item?.summary_currentAdjustDedCode6,
  item?.summary_currentAdjustDedAmount6,
  item?.summary_currentAdjustDedCode7,
  item?.summary_currentAdjustDedAmount7,
  item?.summary_currentAdjustDedCode8,
  item?.summary_currentAdjustDedAmount8,
];

const buildCurrentReportCsv = (overAllData: any[]): any[][] => {
  const rows: any[][] = [[...csvData, 'Adjusted Hour']];
  overAllData.forEach((item: any, index: number) => {
    rows.push(buildCurrentReportRow(item, index));
  });
  return rows;
};

const buildAdpReportCsv = (overAllData: any[]): any[][] => {
  const rows: any[][] = [csvAdpData];
  overAllData.forEach((item: any) => {
    rows.push(buildAdpReportRow(item));
  });
  return rows;
};

const buildOriginalReportCsv = (overAllData: any[]): any[][] => {
  const rows: any[][] = [csvData];
  overAllData.forEach((item: any, index: number) => {
    rows.push(buildOriginalReportRow(item, index));
  });
  return rows;
};

// ---------------------------------------------------------------------------
// Summary → API payload mapper
// ---------------------------------------------------------------------------

/**
 * Multiple edits may be emitted for the same row. Fold them into a single
 * entry per `summary_id`, with later edits taking precedence.
 */
const mergeListBySummaryId = (payrollSummary: any[]): any[] => {
  const mergedData: any[] = [];
  payrollSummary.forEach((data: any) => {
    const index = mergedData.findIndex(
      (entry: any) => entry.summary_id === data.summary_id,
    );
    if (index === -1) {
      mergedData.push(data);
    } else {
      mergedData[index] = { ...mergedData[index], ...data };
    }
  });
  return mergedData;
};

/**
 * Map a (merged) table row into the shape expected by the payroll-summary
 * PUT endpoint.
 */
const toPayrollSummaryPayload = (item: any) => ({
  additionalPay: item.summary_additionalPay,
  adjustDedAmount1: item.summary_adjustDedAmount1,
  adjustDedAmount2: item.summary_adjustDedAmount2,
  adjustDedAmount3: item.summary_adjustDedAmount3,
  adjustDedAmount4: item.summary_adjustDedAmount4,
  adjustDedAmount5: item.summary_adjustDedAmount5,
  adjustDedAmount6: item.summary_adjustDedAmount6,
  adjustDedAmount7: item.summary_adjustDedAmount7,
  adjustDedAmount8: item.summary_adjustDedAmount8,
  adjustDedCode1: item.summary_adjustDedCode1,
  adjustDedCode2: item.summary_adjustDedCode2,
  adjustDedCode3: item.summary_adjustDedCode3,
  adjustDedCode4: item.summary_adjustDedCode4,
  adjustDedCode5: item.summary_adjustDedCode5,
  adjustDedCode6: item.summary_adjustDedCode6,
  adjustDedCode7: item.summary_adjustDedCode7,
  adjustDedCode8: item.summary_adjustDedCode8,
  basePay: item.summary_basePay,
  baseWage: item.summary_baseWage,
  batchId: item.summary_batchId,
  currentAdjustDedAmount1: item.summary_currentAdjustDedAmount1,
  currentAdjustDedAmount2: item.summary_currentAdjustDedAmount2,
  currentAdjustDedAmount3: item.summary_currentAdjustDedAmount3,
  currentAdjustDedAmount4: item.summary_currentAdjustDedAmount4,
  currentAdjustDedAmount5: item.summary_currentAdjustDedAmount5,
  currentAdjustDedAmount6: item.summary_currentAdjustDedAmount6,
  currentAdjustDedAmount7: item.summary_currentAdjustDedAmount7,
  currentAdjustDedAmount8: item.summary_currentAdjustDedAmount8,
  currentAdjustDedCode1: item.summary_currentAdjustDedCode1,
  currentAdjustDedCode2: item.summary_currentAdjustDedCode2,
  currentAdjustDedCode3: item.summary_currentAdjustDedCode3,
  currentAdjustDedCode4: item.summary_currentAdjustDedCode4,
  currentAdjustDedCode5: item.summary_currentAdjustDedCode5,
  currentAdjustDedCode6: item.summary_currentAdjustDedCode6,
  currentAdjustDedCode7: item.summary_currentAdjustDedCode7,
  currentAdjustDedCode8: item.summary_currentAdjustDedCode8,
  currentEarnings3Amount1: item.summary_currentEarnings3Amount1,
  currentEarnings3Amount2: item.summary_currentEarnings3Amount2,
  currentEarnings3Amount3: item.summary_currentEarnings3Amount3,
  currentEarnings3Amount4: item.summary_currentEarnings3Amount4,
  currentEarnings3Amount5: item.summary_currentEarnings3Amount5,
  currentEarnings3Amount6: item.summary_currentEarnings3Amount6,
  currentEarnings3Amount7: item.summary_currentEarnings3Amount7,
  currentEarnings3Amount8: item.summary_currentEarnings3Amount8,
  currentEarnings3Amount9: item.summary_currentEarnings3Amount9,
  currentEarnings3Code1: item.summary_currentEarnings3Code1,
  currentEarnings3Code2: item.summary_currentEarnings3Code2,
  currentEarnings3Code3: item.summary_currentEarnings3Code3,
  currentEarnings3Code4: item.summary_currentEarnings3Code4,
  currentEarnings3Code5: item.summary_currentEarnings3Code5,
  currentEarnings3Code6: item.summary_currentEarnings3Code6,
  currentEarnings3Code7: item.summary_currentEarnings3Code7,
  currentEarnings3Code8: item.summary_currentEarnings3Code8,
  currentEarnings3Code9: item.summary_currentEarnings3Code9,
  currentHours3Amount1: item.summary_currentHours3Amount1,
  currentHours3Amount2: item.summary_currentHours3Amount2,
  currentHours3Amount3: item.summary_currentHours3Amount3,
  currentHours3Amount4: item.summary_currentHours3Amount4,
  currentHours3Code1: item.summary_currentHours3Code1,
  currentHours3Code2: item.summary_currentHours3Code2,
  currentHours3Code3: item.summary_currentHours3Code3,
  currentHours3Code4: item.summary_currentHours3Code4,
  doNotPay: item.summary_doNotPay,
  earnings3Amount1: item.summary_earnings3Amount1,
  earnings3Amount2: item.summary_earnings3Amount2,
  earnings3Amount3: item.summary_earnings3Amount3,
  earnings3Amount4: item.summary_earnings3Amount4,
  earnings3Amount5: item.summary_earnings3Amount5,
  earnings3Amount6: item.summary_earnings3Amount6,
  earnings3Amount7: item.summary_earnings3Amount7,
  earnings3Amount8: item.summary_earnings3Amount8,
  earnings3Amount9: item.summary_earnings3Amount9,
  earnings3Code1: item.summary_earnings3Code1,
  earnings3Code2: item.summary_earnings3Code2,
  earnings3Code3: item.summary_earnings3Code3,
  earnings3Code4: item.summary_earnings3Code4,
  earnings3Code5: item.summary_earnings3Code5,
  earnings3Code6: item.summary_earnings3Code6,
  earnings3Code7: item.summary_earnings3Code7,
  earnings3Code8: item.summary_earnings3Code8,
  earnings3Code9: item.summary_earnings3Code9,
  grossEarnings: item.summary_grossEarnings,
  hours3Amount1: item.summary_hours3Amount1,
  hours3Amount2: item.summary_hours3Amount2,
  hours3Amount3: item.summary_hours3Amount3,
  hours3Code1: item.summary_hours3Code1,
  hours3Code2: item.summary_hours3Code2,
  hours3Code3: item.summary_hours3Code3,
  id: item.summary_id,
  isVerified: item.summary_isVerified,
  notes: item.summary_notes,
  payPeriodId: item.summary_payPeriodId,
  payPeriodYear: item.summary_payPeriodYear,
  project: item.project_customerName,
  psAdjustedAmount: item.summary_psAdjustedAmount,
  regEarnings: item.summary_regEarnings,
  regHours: item?.summary_regHours_orginal
    ? item?.summary_regHours_orginal
    : item.summary_regHours,
});

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const buildSummaryUrl = (
  payPeriodId: string,
  companyStatus: string,
  userFilter?: { userId?: string; payCode?: string },
): string => {
  let url = `${String(PAYSHEET.Payroll)}/summary?payperiodId=${payPeriodId}`;
  if (companyStatus !== 'all') {
    url += `&organization=${companyStatus}`;
  }
  if (userFilter?.userId) {
    url += `&userId=${userFilter.userId}`;
  }
  if (userFilter?.payCode) {
    url += `&paycode=${userFilter.payCode}`;
  }
  return url;
};

const fetchPayrollSummary = (
  payPeriodId: string,
  companyStatus: string,
  userFilter?: { userId?: string; payCode?: string },
) => getAllListData(buildSummaryUrl(payPeriodId, companyStatus, userFilter));

const syncPayment = (payPeriodId: string, type: 'itech' | 'smartworks') => {
  const url =
    MASTER_ENDPOINT.PAYMENTPAYROLL +
    (type === 'itech' ? '/itech-payment' : '/smartworks-payment');
  return updateData(payPeriodId, {}, url, true);
};

const createPayPeriodSnapshotApi = (payPeriodId: string) =>
  postRequest({
    data: {},
    url: `${MASTER_ENDPOINT.PayPeriodSnapshot}/${payPeriodId}`,
  });

const updatePayrollSummaryApi = (payrollSummary: any[]) =>
  putRequest({
    data: { payrollSummary },
    url: `${MASTER_ENDPOINT.PAYMENTPAYROLL}`,
  });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const actionRowStyles = {
  closeButton: { color: 'text.secondary', ml: 1 },
  closeIcon: { fontSize: 28, fontWeight: 'bold' },
  container: { alignItems: 'center', display: 'flex' },
  lockButton: { color: '#292929', ml: 1 },
  menuButton: { ml: 1 },
};

const PayPeriodAccordian = forwardRef((props: AccordianProps, ref: any) => {
  const { item, index, expanded, setExpanded, userList } = props;

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [expandView, setExpandView] = React.useState('');
  const [overAllData, setOverAllData] = React.useState<any[]>([]);
  const [csvDataState, setCsvDataState] = React.useState<{
    currentReportData: any;
    adpReportData: any;
    originalReportData: any;
  }>({
    adpReportData: [],
    currentReportData: [],
    originalReportData: [],
  });
  const [param, setParam] = React.useState<any>({ reload: true, skip: 0 });
  const [userFilter, setUserFilter] = React.useState<any>({
    payCode: '',
    userId: '',
  });
  const [summaryData, setSummaryData] = React.useState<{
    loader: boolean;
    data: any;
    total: number;
  }>({
    data: [],
    loader: true,
    total: 0,
  });
  const [updatedSummaryTableData, setUpdatedSummaryTableData] = React.useState<
    any[]
  >([]);
  const [isSyncPaymentLocked, setIsSyncPaymentLocked] = React.useState({
    isLocked: false,
    payperiodId: '',
  });

  const [openUser, setOpenUser] =
    React.useState<PopperState>(INITIAL_POPPER_STATE);
  const [openPayCode, setPayCode] =
    React.useState<PopperState>(INITIAL_POPPER_STATE);
  const [openPayRollSummary, setOpenPayRollSummary] =
    React.useState<PopperState>(INITIAL_POPPER_STATE);

  const { companyStatus }: any = useAppSelector(
    (store: any) => store.userLogin,
  );

  const isExpanded = expanded === item.id;
  const hasPendingEdits = updatedSummaryTableData.length > 0;
  const canSyncPayment =
    companyStatus === 'smartworks' || companyStatus === 'itech';

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const getPanelApi = React.useCallback(
    async (userFilterData: any, _paramData: any, currentCompany: string) => {
      setSummaryData((pre) => ({ ...pre, data: [], loader: true }));
      try {
        const resp: any = await fetchPayrollSummary(
          item.id,
          currentCompany,
          userFilterData,
        );
        setSummaryData((pre) => ({
          ...pre,
          data: resp?.data,
          loader: false,
          total: resp?.total,
        }));
      } catch {
        setSummaryData((pre) => ({ ...pre, loader: false }));
      }
    },
    [item.id],
  );

  useImperativeHandle(ref, () => ({
    reloadData() {
      setParam({ ...param, reload: !param.reload });
    },
  }));

  const getEndPayrollSummary = React.useCallback(async () => {
    const url = `${MASTER_ENDPOINT.PAYMENTPAYROLL}/end/${
      item?.id as string
    }?organization=${String(companyStatus)}`;
    const response = await getEndPayrollSummaryApi(url);
    if (response) {
      setIsSyncPaymentLocked((prev) => ({
        ...prev,
        isLocked: response.endPayrollSummary,
        payperiodId: item?.id as string,
      }));
    }
  }, [item?.id, companyStatus]);

  useEffect(() => {
    sessionStorage.removeItem('payrollSummaryTableEdits');
    if (param && Object.keys(param).length && expanded && companyStatus) {
      getPanelApi(userFilter, param, companyStatus);
      getEndPayrollSummary();
    }
  }, [
    expanded,
    getPanelApi,
    param,
    userFilter,
    companyStatus,
    getEndPayrollSummary,
  ]);

  // -------------------------------------------------------------------------
  // Action handlers
  // -------------------------------------------------------------------------

  const handleSyncPaymentLockToggle = async (event: React.MouseEvent) => {
    event.stopPropagation();

    const nextLocked = !isSyncPaymentLocked.isLocked;

    setIsSyncPaymentLocked((prev) => ({
      ...prev,
      isLocked: nextLocked,
      payperiodId: item?.id as string,
    }));

    const url = `${MASTER_ENDPOINT.PAYMENTPAYROLL}/end/${item?.id as string}`;
    const reqBody = {
      endPayrollSummary: nextLocked,
      organization: companyStatus,
    };

    try {
      await endSummaryApi(url, reqBody);
      // Re-read the authoritative value from the server so the icon stays in
      // sync with the persisted endPayrollSummary flag.
      await getEndPayrollSummary();

      const dateRange = `${moment(item.startDate).format('MMM DD')} - ${moment(
        item.endDate,
      ).format('MMM DD, YYYY')}`;
      toast.success(
        `Payroll from ${dateRange} is ${nextLocked ? 'locked' : 'unlocked'}`,
      );
    } catch {
      // Roll back the optimistic update if the PUT failed.
      setIsSyncPaymentLocked((prev) => ({ ...prev, isLocked: !nextLocked }));
    }
  };

  const handlePayrollTableRowEdit = (row: any) => {
    setUpdatedSummaryTableData([...updatedSummaryTableData, row]);
  };

  const handleCancelEdits = () => {
    sessionStorage.removeItem('payrollSummaryTableEdits');
  };

  const handleSyncPayment = (event: React.MouseEvent) => {
    event.stopPropagation();
    syncPayment(expanded, companyStatus as 'itech' | 'smartworks').then(() => {
      // Re-pull the summary so the table reflects the sync result.
      getPanelApi(userFilter, param, companyStatus);
    });
  };

  const openActionsMenu =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setOpenPayRollSummary({
        anchorEl: event.currentTarget,
        open: !openPayCode.open,
        placement: newPlacement,
      });
      if (!overAllData.length) {
        fetchPayrollSummary(expanded, companyStatus).then((resp: any) => {
          setOverAllData(resp?.data);
        });
      }
    };

  const handleCreatePayPeriodSnapshot = async (payPeriodId: string) => {
    try {
      const response: any = await createPayPeriodSnapshotApi(payPeriodId);
      if (response?.snapshotVersion) {
        toast.success(
          `Pay Period Snapshot Version ${
            response.snapshotVersion as string
          } created successfully`,
        );
      } else {
        toast.error('Failed to create Pay Period Snapshot');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setOpenPayRollSummary((pre) => ({ ...pre, open: false }));
    }
  };

  const savePayrollSummary = async () => {
    const rawEdits = JSON.parse(
      sessionStorage.getItem('payrollSummaryTableEdits') || '[]',
    );
    const mergedData = mergeListBySummaryId(rawEdits);
    const formattedData = mergedData.map(toPayrollSummaryPayload);

    try {
      const response = await updatePayrollSummaryApi(formattedData);
      if (response) {
        toast.success('Payroll Summary updated successfully');
        sessionStorage.removeItem('payrollSummaryTableEdits');
        setUpdatedSummaryTableData([]);
      } else {
        toast.error('Failed to update Payroll Summary');
      }
    } catch (error) {
      toast.error('Failed to update Payroll Summary');
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderPendingEditsActions = () => {
    if (!hasPendingEdits) return null;
    return (
      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleCancelEdits} sx={secondaryButtonSx}>
          Cancel
        </Button>
        <Button onClick={savePayrollSummary} sx={primaryButtonSx}>
          Save
        </Button>
      </span>
    );
  };

  const renderSyncPaymentButton = () => {
    if (!canSyncPayment) return null;
    return (
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
        }}>
        <Button onClick={handleSyncPayment} sx={syncPaymentButtonSx}>
          Sync payment
        </Button>
      </span>
    );
  };

  const renderAccordionHeader = () => (
    <AccordionSummary
      aria-controls="panel1a-content"
      onClick={() => setExpanded(item.id)}
      id="panel1a-header"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingLeft: '20px',
        width: '100%',
      }}>
      <Typography
        sx={{ width: '45% !important' }}
        component={'div'}
        className="accordin-details">
        <Typography component={'p'}>
          {moment(item.startDate).format('MMM')}{' '}
          <strong>{moment(item.startDate).format('DD')}</strong> -{' '}
          {moment(item.endDate).format('MMM')}{' '}
          <strong>{moment(item.endDate).format('DD')}</strong>
          &nbsp;&nbsp;&nbsp;
          <strong>{moment(item.endDate).format('YYYY')}</strong>
        </Typography>
      </Typography>

      <Typography sx={{ width: '50% !important' }} component={'div'}>
        {isExpanded && (
          <Typography
            sx={{ display: 'flex', width: '100%' }}
            justifyContent={'end'}
            alignItems={'center'}>
            {renderPendingEditsActions()}
            {renderSyncPaymentButton()}
          </Typography>
        )}
      </Typography>

      <Typography
        component={'p'}
        sx={{
          alignItems: 'flex-end !important',
          alignSelf: 'center !important',
          display: 'flex !important',
          justifyContent: 'flex-end !important',
        }}>
        {isExpanded && canSyncPayment && (
          <IconButton
            aria-label={
              isSyncPaymentLocked.isLocked
                ? 'Unlock pay period'
                : 'Lock pay period'
            }
            className="dropdown-btn"
            onClick={handleSyncPaymentLockToggle}
            size="small"
            sx={{ color: '#292929', ml: 2 }}>
            {isSyncPaymentLocked.isLocked ? (
              <LockIcon fontSize="small" />
            ) : (
              <LockOpenIcon fontSize="small" />
            )}
          </IconButton>
        )}
        {isExpanded && (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setExpandView(expandView === item.id ? '' : item.id);
            }}
            className="dropdown-btn"
            size="small"
            sx={{ ml: 2 }}
            aria-label="Open payroll table in full window"
            aria-expanded={expandView === item.id}>
            <img src={ExpandPng} style={{ height: 20, width: 20 }} alt="" />
          </IconButton>
        )}
        <IconButton
          onClick={openActionsMenu('bottom-end')}
          className="dropdown-btn"
          size="small"
          sx={{ ml: 2 }}
          aria-haspopup="true">
          <MoreVertIcon />
        </IconButton>
      </Typography>
    </AccordionSummary>
  );

  const renderAccordionBody = () => {
    if (!isExpanded) return null;
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography
            component={'div'}
            style={{ display: 'grid', width: '100%' }}
            className="common-table">
            {summaryData.loader ? (
              <Typography
                component={'div'}
                sx={{
                  justifyContent: 'center',
                  paddingBottom: '15px',
                  paddingTop: '5px',
                  textAlign: 'center',
                }}>
                <CircularProgress sx={{ color: '#340000' }} />
              </Typography>
            ) : (
              <PayrollTable
                summaryTableData={summaryData.data.map(
                  (row: any, rowIndex: number) => ({
                    ...row,
                    summaryDataIndex: rowIndex,
                  }),
                )}
                hiddenColumns={props.hiddenColumns}
                handleRowEdit={handlePayrollTableRowEdit}
                companyName={companyStatus}
                isLocked={isSyncPaymentLocked.isLocked}
              />
            )}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderExpandDialog = () => (
    <Dialog
      open={expandView === item.id}
      onClose={() => setExpandView('')}
      maxWidth={false}
      fullWidth
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 8,
          height: 'min(96vh, 1100px)',
          maxWidth: 'min(99vw, 1920px)',
          width: '100%',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          pr: 1,
        }}>
        <Typography component="span" variant="subtitle1" fontWeight={600}>
          {moment(item.startDate).format('MMM')}{' '}
          <strong>{moment(item.startDate).format('DD')}</strong> –{' '}
          {moment(item.endDate).format('MMM')}{' '}
          <strong>{moment(item.endDate).format('DD')}</strong>
          {', '}
          <strong>{moment(item.endDate).format('YYYY')}</strong>
        </Typography>
        <Typography component={'div'} sx={actionRowStyles.container}>
          {renderPendingEditsActions()}
          {renderSyncPaymentButton()}
          <IconButton
            aria-label={
              isSyncPaymentLocked.isLocked
                ? 'Unlock pay period'
                : 'Lock pay period'
            }
            className="dropdown-btn"
            onClick={handleSyncPaymentLockToggle}
            size="small"
            sx={actionRowStyles.lockButton}>
            {isSyncPaymentLocked.isLocked ? (
              <LockIcon fontSize="small" />
            ) : (
              <LockOpenIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            aria-haspopup="true"
            aria-label="More actions"
            className="dropdown-btn"
            onClick={openActionsMenu('bottom-end')}
            size="small"
            sx={actionRowStyles.menuButton}>
            <MoreVertIcon />
          </IconButton>
          <IconButton
            aria-label="Close"
            onClick={() => setExpandView('')}
            size="small"
            sx={actionRowStyles.closeButton}>
            <CloseIcon sx={actionRowStyles.closeIcon} />
          </IconButton>
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2,
          pt: 0,
        }}>
        {summaryData.loader ? (
          <Typography
            component="div"
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 320,
            }}>
            <CircularProgress sx={{ color: '#340000' }} />
          </Typography>
        ) : (
          <PayrollTable
            summaryTableData={summaryData.data.map((row: any, idx: number) => ({
              ...row,
              summaryDataIndex: idx,
            }))}
            hiddenColumns={props.hiddenColumns}
            handleRowEdit={handlePayrollTableRowEdit}
            companyName={companyStatus}
            containerHeight="calc(min(96vh, 1100px) - 148px)"
            isLocked={isSyncPaymentLocked.isLocked}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const renderUserFilterPopper = () => {
    if (!openUser.open) return null;
    return (
      <ClickAwayListener
        onClickAway={() =>
          setOpenUser((pre) => ({ ...pre, open: !openUser.open }))
        }>
        <Popper
          className="popwidget1"
          open={openUser.open}
          anchorEl={openUser.anchorEl}
          placement={openUser.placement}
          transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <Typography sx={{ p: 2 }}>
                  <Autocomplete
                    selectOnFocus
                    clearOnBlur
                    freeSolo
                    onChange={(_e: any, reson: any) => {
                      if (reson) {
                        setUserFilter({ payCode: '', userId: reson.id });
                        setOpenUser((pre) => ({
                          ...pre,
                          open: !openUser.open,
                        }));
                        setParam({ reload: !param.reload, skip: 0 });
                      } else {
                        setUserFilter({ payCode: '', userId: '' });
                      }
                    }}
                    handleHomeEndKeys
                    id="combo-box-demo"
                    options={userList}
                    getOptionLabel={(option) =>
                      option.firstName + ' ' + option.lastName
                    }
                    sx={{ width: 250 }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select" />
                    )}
                  />
                </Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
    );
  };

  const renderPayCodeFilterPopper = () => {
    if (!openPayCode.open) return null;
    return (
      <ClickAwayListener
        onClickAway={() =>
          setPayCode((pre) => ({ ...pre, open: !openPayCode.open }))
        }>
        <Popper
          className="popwidget1"
          open={openPayCode.open}
          anchorEl={openPayCode.anchorEl}
          placement={openPayCode.placement}
          transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <Typography sx={{ p: 2 }}>
                  <Autocomplete
                    selectOnFocus
                    clearOnBlur
                    freeSolo
                    onChange={(_e: any, reson: any) => {
                      if (reson) {
                        setUserFilter({ payCode: reson.id, userId: '' });
                        setParam({ reload: !param.reload, skip: 0 });
                        setPayCode((pre) => ({
                          ...pre,
                          open: !openPayCode.open,
                        }));
                      } else {
                        setUserFilter({ payCode: '', userId: '' });
                      }
                    }}
                    handleHomeEndKeys
                    id="combo-box-demo"
                    options={Object.values(PAYCODETYPES)}
                    getOptionLabel={(option: any) => option.name}
                    sx={{ width: 250 }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select" />
                    )}
                  />
                </Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
    );
  };

  const renderActionsMenu = () => {
    if (!openPayRollSummary.open) return null;
    const filename = getCsvFilename(companyStatus);
    return (
      <ClickAwayListener
        onClickAway={() =>
          setOpenPayRollSummary((pre) => ({
            ...pre,
            open: !openPayRollSummary.open,
          }))
        }>
        <Popper
          className="popwidget"
          open={openPayRollSummary.open}
          anchorEl={openPayRollSummary.anchorEl}
          placement={openPayRollSummary.placement}
          transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <Typography sx={{ p: 2 }}>
                  <p style={{ margin: 0, padding: 10, paddingTop: 15 }}>
                    <span
                      onClick={() => handleCreatePayPeriodSnapshot(item.id)}
                      style={{ ...popperMenuLinkStyle, cursor: 'pointer' }}>
                      Create Pay Period Snapshot
                    </span>
                  </p>
                  <p style={{ margin: 0, padding: 10, paddingTop: 5 }}>
                    <CSVLink
                      style={popperMenuLinkStyle}
                      filename={filename}
                      data={csvDataState.currentReportData}
                      asyncOnClick={true}
                      onClick={(_event, done) => {
                        setCsvDataState((pre) => ({
                          ...pre,
                          currentReportData: buildCurrentReportCsv(overAllData),
                        }));
                        done(true);
                      }}>
                      Download Current Report
                    </CSVLink>
                  </p>
                  <p style={{ margin: 0, padding: 10, paddingTop: 5 }}>
                    <CSVLink
                      style={popperMenuLinkStyle}
                      filename={filename}
                      data={csvDataState.adpReportData}
                      asyncOnClick={true}
                      onClick={(_event, done) => {
                        setCsvDataState((pre) => ({
                          ...pre,
                          adpReportData: buildAdpReportCsv(overAllData),
                        }));
                        done(true);
                      }}>
                      Generate ADP File
                    </CSVLink>
                  </p>
                  <p
                    style={{
                      margin: 0,
                      padding: 10,
                      paddingBottom: 15,
                      paddingTop: 5,
                    }}>
                    <CSVLink
                      style={popperMenuLinkStyle}
                      filename={filename}
                      data={csvDataState.originalReportData}
                      asyncOnClick={true}
                      onClick={(_event, done) => {
                        setCsvDataState((pre) => ({
                          ...pre,
                          originalReportData:
                            buildOriginalReportCsv(overAllData),
                        }));
                        done(true);
                      }}>
                      Download Original Report
                    </CSVLink>
                  </p>
                </Typography>
              </Paper>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <>
      <Accordion key={index} expanded={isExpanded}>
        {renderAccordionHeader()}
        <AccordionDetails sx={{ padding: '0px' }}>
          {renderAccordionBody()}
        </AccordionDetails>
      </Accordion>
      {renderExpandDialog()}
      {renderUserFilterPopper()}
      {renderPayCodeFilterPopper()}
      {renderActionsMenu()}
    </>
  );
});

export default React.memo(PayPeriodAccordian);
